import { useState, useRef } from 'react';
import { Container, Row, Col, Button, Input } from 'reactstrap';
import { toast } from 'react-toastify';
import { isMobile } from 'react-device-detect';
import { AutoSizer, Column, Table } from 'react-virtualized';
import { useDispatch } from 'react-redux';
import { Menu, MenuItem, InputLabel } from '@mui/material';
import useFiltersWidget from '../../../sharedComponents/Filters/useFiltersWidget';
import { BsThreeDotsVertical, BsPencilSquare } from 'react-icons/bs';
import { FaRegTrashAlt, FaRegStar, FaStar, FaFilter } from 'react-icons/fa';
import FilterSetDeleteModal from '../../../sharedComponents/Filters/FilterSetDeleteModal';
import { colors, selectStyles } from '../../../config/constants';
import SelectAllSearch from '../../../sharedComponents/Select/SelectAllSearch';
import LocalStorage from '../../../sharedComponents/LocalStorage/LocalStorage';
import { showModal } from '../../../reducers/ModalsReducer/ModalsActions';
import FilterSetUpdateModal from '../../../sharedComponents/Filters/FilterSetUpdateModal';

const FilterSetsSettings = (props) => {
    const dispatch = useDispatch();
    const userId = LocalStorage.userData.get()?.user?.id;
    const {
        savedFilterSets, deleteFilterSet, setFilterSetAsDefault,
        saveFilterSet, getSelectedFilters,
        selectedCategory, setSelectedCategory,
        selectedSegment, setSelectedSegments,
        selectedCountries, setSelectedCountries,
        selectedStates, setSelectedStates,
        selectedParentLocation, setSelectedParentLocation,
        selectedChildLocation, setSelectedChildLocation,
        allFilters, filterLoading,
        childrenFilters, childrenLoading,
    } = useFiltersWidget();
    const userFilterSets = savedFilterSets?.filter(set => set.user_id === userId).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) ?? []; 
    const filterSetCount = userFilterSets?.length;
    const [isFilterWidgetOpen, setIsFilterWidgetOpen] = useState(false);
    const [isEditingFilter, setIsEditingFilter] = useState(false);
    const [actionMenuPosition, setActionMenuPosition] = useState(null);
    const [activeActionOption, setActiveActionOption] = useState(null);
    const [activeFilterSet, setActiveFilterSet] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [statesActive, setStatesActive] = useState(false);
    const [filterSetName, setFilterSetName] = useState('');
    const filterSetsScrollRef = useRef(null);
    const selectedFilters = getSelectedFilters();

    const getViewportSafeMenuPosition = (rect) => {
        const menuWidth = 176;
        const menuHeight = 140;
        const viewportPadding = 8;
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

        const preferredLeft = Math.round(rect.right);
        const preferredTop = Math.round(rect.top);

        const maxLeft = Math.max(viewportPadding, viewportWidth - menuWidth - viewportPadding);
        const maxTop = Math.max(viewportPadding, viewportHeight - menuHeight - viewportPadding);

        return {
            left: Math.min(Math.max(preferredLeft, viewportPadding), maxLeft),
            top: Math.min(Math.max(preferredTop, viewportPadding), maxTop)
        };
    };

    const resetFilterSetEditor = () => {
        setIsEditingFilter(false);
        setIsFilterWidgetOpen(false);
        setFilterSetName('');
        setActiveFilterSet(null);
        setStatesActive(false);
        setSelectedCategory([]);
        setSelectedSegments([]);
        setSelectedCountries([]);
        setSelectedStates([]);
        setSelectedParentLocation([]);
        setSelectedChildLocation([]);
    };

    const handleDotsClick = (event, rowData) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setActionMenuPosition(getViewportSafeMenuPosition(rect));
        setActiveActionOption({ value: rowData.id, label: rowData.name });
        setActiveFilterSet(rowData);
    };

    const handleActionMenuClose = (event) => {
        if (event) event.stopPropagation();
        setActionMenuPosition(null);
    };

    const handleEditClick = (event) => {
        if (event) event.stopPropagation();
        setActionMenuPosition(null);
        setIsEditingFilter(true);
        setIsFilterWidgetOpen(true);

        window.requestAnimationFrame(() => {
            filterSetsScrollRef.current?.scrollTo({ top: 0 });
        });

        const filterSet = savedFilterSets?.find(set => set.id === activeActionOption.value);
        setActiveFilterSet(filterSet);
        setFilterSetName(filterSet.name);
        setSelectedCategory(filterSet.filters.categories?.map(category => ({ value: category, label: category })));
        setSelectedSegments(filterSet.filters.segments?.map(segment => ({ value: segment, label: segment.split('_').map(w => w[0].toUpperCase() + w.substring(1).toLowerCase()).join(' ') })));
        setSelectedCountries(filterSet.filters.countries?.map(country => ({ value: country, label: country })));
        setSelectedStates(filterSet.filters.states?.map(state => ({ value: state, label: state })));
        setSelectedParentLocation(filterSet.filters.locations?.map(location => ({ value: location, label: location })));
        setSelectedChildLocation(filterSet.filters.children?.map(location => ({ value: location, label: location })));
    };

    const handleDeleteClick = (event) => {
        if (event) event.stopPropagation();
        setActionMenuPosition(null);
        setIsDeleteModalOpen(true);
    };

    const handleSetDefaultClick = (event) => {
        if (event) event.stopPropagation();
        setActionMenuPosition(null);
        setFilterSetAsDefault(activeActionOption?.value);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setActiveActionOption(null);
    };

    return (
        <>
        <Container fluid className={(isMobile ? '':'px-2') + ' mb-4'} style={{display: 'flex', flexDirection: 'column', height: '78%'}}>
            <Row className='mt-4 mb-1'>
                <Col className='ps-3'>
                    <Row>
                        <Col className="main-header text-size-16 px-0">
                            Filters ({filterSetCount})
                        </Col>
                    </Row>
                    <Row>
                        <Col className="text-size-12 px-0">
                            To create a Filter Set, click "Create New Filter Set"
                        </Col>
                    </Row>
                </Col>
                {!isFilterWidgetOpen &&
                <Col className='w-fc pe-1' style={{alignSelf: 'center'}}>
                    <Button
                        className='button-label w-fc py-1 px-3'
                        color='blueFilter'
                        style={{color: 'white'}}
                        bsSize='sm'
                        onClick={() => {
                            setIsFilterWidgetOpen(true);
                        }}>
                        Create New Filter Set
                    </Button>
                </Col>
                }
            </Row>
            <Row ref={filterSetsScrollRef} className='mt-0' style={{flexGrow: 1, minHeight: '200px', overflowY: 'auto', padding: '2px'}}>
                <Col>
                    {isFilterWidgetOpen && (
                    <Row className='mt-0 pb-2' style={{background: 'white', border: `1px solid ${colors.grayLight}`, borderRadius: '5px'}} >
                        <Col className='py-0 my-0'>
                            <Row className='py-1' style={{background: colors.grayNew, borderRadius: '5px 5px 0 0', borderBottom: `1px solid ${colors.grayLight}`, alignItems: 'center'}}>
                                <Col className='main-header text-size-16 py-2'>
                                    <FaFilter className='me-2' color={colors.blueFilter}/>
                                    {isEditingFilter ? 'Edit' : 'Create New'} Filter Set
                                </Col>
                                {isFilterWidgetOpen &&
                                <>
                                    <Col className='w-fc px-0'>
                                        <Button
                                            className='button-label py-1 px-2'
                                            color='mutedRed'
                                            bsSize='sm'
                                            outline={true}
                                            onClick={() => {
                                                resetFilterSetEditor();
                                            }}>
                                            Cancel
                                        </Button>
                                    </Col>
                                    <Col className='w-fc'>
                                        {!isEditingFilter &&
                                        <Button
                                            className='button-label py-1 px-2'
                                            color='blueFilter'
                                            style={{color: 'white'}}
                                            bsSize='sm'
                                            disabled={!filterSetName.trim()}
                                            onClick={() => {
                                                saveFilterSet(filterSetName.trim(), getSelectedFilters());
                                                setFilterSetName('');
                                                setIsFilterWidgetOpen(false);
                                                toast.success('Filter Set Saved Successfully');
                                            }}
                                        >
                                            Save Filter Set
                                        </Button>
                                        }
                                        {isEditingFilter &&
                                        <Button
                                            className='button-label py-1 px-2'
                                            color='blueFilter'
                                            style={{color: 'white'}}
                                            bsSize='sm'
                                            disabled={!filterSetName.trim()}
                                            onClick={() => {
                                                dispatch(showModal(FilterSetUpdateModal, {
                                                    filterSet: activeFilterSet?.id ? {
                                                        value: activeFilterSet.id,
                                                        label: filterSetName.trim()
                                                    } : null,
                                                    existingFilters: activeFilterSet?.filters,
                                                    existingFilterSetName: activeFilterSet?.name,
                                                    selectedFilters,
                                                    onClose: resetFilterSetEditor
                                                }));
                                            }}
                                        >
                                            Save Changes
                                        </Button>
                                        }
                                    </Col>
                                </>
                                }
                            </Row>
                            <Row className='mt-1 px-2'>
                                <Col>
                                    <InputLabel style={{color: 'black', fontSize: '12px'}}>Filter Set Name</InputLabel>
                                    <Input
                                        size='md'
                                        placeholder='Enter Filter Set Name'
                                        value={filterSetName}
                                        onChange={(e) => setFilterSetName(e.target.value)}
                                    />
                                </Col>
                            </Row>
                            <Row className='mt-0 px-2 pb-1 g-2' >
                                <Col style={{minWidth: '200px'}}>
                                    <InputLabel style={{color: 'black', fontSize: '12px'}}>Category</InputLabel>
                                    <SelectAllSearch
                                        placeholder='Select Category'
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        isLoading={filterLoading}
                                        options={allFilters?.categories?.sort()?.map((category) => ({value: category, label: category})) || []}
                                        value={selectedCategory}
                                        onChange={(category) => setSelectedCategory(category)}
                                    />
                                </Col>
                                <Col style={{minWidth: '200px'}}>
                                    <InputLabel style={{color: 'black', fontSize: '12px'}}>Customer Segment</InputLabel>
                                    <SelectAllSearch
                                        placeholder='Select Segment'
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        isLoading={filterLoading}
                                        options={allFilters?.segments?.sort()?.map((segment) => {
                                            const segmentLabel = segment.split('_').map(w => w[0].toUpperCase() + w.substring(1).toLowerCase()).join(' ');
                                            return {value: segment, label: segmentLabel};
                                        }) || []}
                                        value={selectedSegment}
                                        onChange={(segment) => setSelectedSegments(segment)}
                                    />
                                </Col>
                                <Col style={{minWidth: '200px'}}>
                                    <InputLabel style={{color: 'black', fontSize: '12px'}}>Country</InputLabel>
                                    <SelectAllSearch
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        name='country'
                                        styles={selectStyles}
                                        options={allFilters?.countries?.sort()?.map((country) => ({value: country, label: country})) || []}
                                        onChange={(e) => {
                                            setStatesActive(e && e.length > 0 && e.some(c => c.value === 'US'));
                                            setSelectedCountries(e);
                                            if (!e || !e.some(c => c.value === 'US')) {
                                                setSelectedStates([]);
                                            }
                                        }}
                                        value={selectedCountries}
                                        placeholder='Select Country'
                                    />
                                </Col>
                                <Col style={{minWidth: '200px'}}>
                                    <InputLabel style={{color: 'black', fontSize: '12px'}}>State/Territory</InputLabel>
                                    <SelectAllSearch
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        name='state'
                                        styles={selectStyles}
                                        options={allFilters?.states?.sort()?.map((state) => ({value: state, label: state})) || []}
                                        onChange={(e) => setSelectedStates(e)}
                                        value={selectedStates}
                                        placeholder='Select State/Territory'
                                        isDisabled={!selectedCountries || selectedCountries.length === 0 || !statesActive}
                                    />
                                </Col>
                                <Col style={{minWidth: '200px'}}>
                                    <InputLabel style={{color: 'black', fontSize: '12px'}}>Parent Location</InputLabel>
                                    <SelectAllSearch
                                        placeholder='Select Parent Location'
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        isLoading={filterLoading}
                                        options={allFilters?.locations?.sort()?.map((location) => ({value: location, label: location})) || []}
                                        value={selectedParentLocation}
                                        onChange={(location) => setSelectedParentLocation(location)}
                                    />
                                </Col>
                                <Col style={{minWidth: '200px'}}>
                                    <InputLabel style={{color: 'black', fontSize: '12px'}}>Child Location</InputLabel>
                                    <SelectAllSearch
                                        placeholder='Select Child Location'
                                        isMulti={true}
                                        isDisabled={!selectedParentLocation || selectedParentLocation.length === 0}
                                        closeMenuOnSelect={false}
                                        isLoading={childrenLoading || filterLoading}
                                        options={childrenFilters?.sort()?.map((child) => ({value: child, label: child})) || []}
                                        value={selectedChildLocation}
                                        onChange={(child) => setSelectedChildLocation(child)}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    )}
                    {filterSetCount === 0 && !isFilterWidgetOpen &&
                        <Row className='mt-3' style={{flexGrow: 1}}>
                            <Col className='w-fc text-center text-gray' style={{height: '100%'}}>
                                No filter sets created yet.
                            </Col>
                        </Row>
                    }
                    {filterSetCount > 0 && !isFilterWidgetOpen &&
                    <Row className='py-2' style={{height: '100%'}}>
                        <Col className='px-0'>
                            <AutoSizer>
                                {({ height, width }) => (
                                    <Table
                                        className='table border-grayLight'
                                        width={width}
                                        height={height}
                                        headerHeight={34.5}
                                        rowHeight={38}
                                        rowCount={userFilterSets.length}
                                        rowGetter={({ index }) => userFilterSets[index]}
                                    >   
                                        <Column
                                            label='Filter Set Name'
                                            dataKey='name'
                                            width={width * 0.5}
                                            cellRenderer={({ cellData, rowData }) => (
                                                <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                                    {rowData.isDefault && <FaStar color={colors.yellow} />}
                                                    <span>{cellData}</span>
                                                </div>
                                            )}
                                        />
                                        <Column
                                            label='Created Date'
                                            dataKey='createdAt'
                                            width={width * 0.4}
                                            cellRenderer={({ cellData }) => (
                                                <span>{new Date(cellData).toLocaleDateString()}</span>
                                            )}
                                        />
                                        <Column
                                            label='Actions'
                                            dataKey='actions'
                                            width={width * 0.1}
                                            cellRenderer={({ rowData }) => (
                                                <div style={{justifySelf: 'center'}}>
                                                    <BsThreeDotsVertical
                                                        style={{cursor: 'pointer'}}
                                                        onClick={(e) => handleDotsClick(e, rowData)}
                                                    />
                                                </div>
                                            )}
                                            headerRenderer={({label}) => (
                                                <div style={{display: 'flex', justifyContent: 'center', backgroundColor: 'transparent', color: '#76777B'}}>
                                                    {label}
                                                </div>
                                            )}
                                        />  
                                    </Table>
                                )}
                            </AutoSizer>
                        </Col>
                    </Row>
                    }
                </Col>
            </Row>
        </Container>
        <Menu
            id="filter-set-row-menu"
            anchorReference="anchorPosition"
            anchorPosition={actionMenuPosition || { top: 0, left: 0 }}
            open={Boolean(actionMenuPosition)}
            onClose={handleActionMenuClose}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { ml: 0.5, minWidth: 168 } } }}
        >
            {!savedFilterSets?.find(set => set.id === activeActionOption?.value)?.isDefault &&
                <MenuItem onClick={handleSetDefaultClick}><span>Set as Default</span><FaRegStar className='ms-auto' color={colors.yellow}/></MenuItem>
            }
            <MenuItem onClick={handleEditClick}><span>Edit</span><BsPencilSquare className='ms-auto' color={colors.blueFilter}/></MenuItem>
            <MenuItem onClick={handleDeleteClick}><span>Delete</span><FaRegTrashAlt className='ms-auto' color='red'/></MenuItem>
        </Menu>
        <FilterSetDeleteModal
            isOpen={isDeleteModalOpen}
            option={activeActionOption}
            onConfirm={({id}) => deleteFilterSet(id)}
            onClose={closeDeleteModal}
        />
        </>
    )
}

export default FilterSetsSettings;
